/**
 * Deck statistics computation
 *
 * Pure functions computing mana curve, type breakdown, and color distribution
 * from enriched deck cards. Consumed by `useDeckStatistics` (UI) and the AI
 * chat exporter.
 */
import { countManaPips, getDisplayManaCost } from '@/lib/cardUtils';
import type { Color, TypeCategory } from '@/types/card';
import type { EnrichedDeckCard } from '@/types/deck';

// ============================================================================
// Types
// ============================================================================

export interface ManaCurveData {
  /** Card count by CMC (0-7, where 7 = 7+) */
  distribution: number[];
  /** Average CMC (excluding lands) */
  average: number;
  /** Median CMC (excluding lands) */
  median: number;
  /** Total non-land cards */
  totalNonLandCards: number;
}

export interface TypeBreakdown {
  creatures: number;
  instants: number;
  sorceries: number;
  artifacts: number;
  enchantments: number;
  planeswalkers: number;
  lands: number;
  battles: number;
  other: number;
  /** Creature vs non-creature ratio (creatures / non-land cards) */
  creatureRatio: number;
}

export interface ColorDistribution {
  /** Mana pip count per color */
  pips: Record<Color | 'C', number>;
  /** Total colored pips */
  totalPips: number;
  /** Cards per color identity */
  cardsByIdentity: Record<Color | 'C' | 'M', number>;
  /** Dominant color (most pips) */
  dominantColor: Color | 'C' | null;
}

export interface DeckStatistics {
  /** Total cards in deck (sum of quantities) */
  totalCards: number;
  /** Unique cards (distinct card entries) */
  uniqueCards: number;
  /** Mana curve analysis */
  manaCurve: ManaCurveData;
  /** Card type breakdown */
  typeBreakdown: TypeBreakdown;
  /** Color distribution from mana costs */
  colorDistribution: ColorDistribution;
  /** Land count */
  landCount: number;
  /** Non-land spell count */
  spellCount: number;
}

/** Zones counted toward deck statistics by default (matches legality counting) */
export const DEFAULT_STATISTICS_ZONES = ['main', 'commander', 'companion'];

// ============================================================================
// Helpers
// ============================================================================

/** Map TypeCategory to TypeBreakdown plural keys */
const TYPE_TO_BREAKDOWN_KEY: Partial<Record<TypeCategory, keyof TypeBreakdown>> = {
  creature: 'creatures',
  planeswalker: 'planeswalkers',
  battle: 'battles',
  instant: 'instants',
  sorcery: 'sorceries',
  artifact: 'artifacts',
  enchantment: 'enchantments',
  land: 'lands',
  other: 'other',
};

/**
 * Calculate median from an array of numbers
 */
function median(values: number[]): number {
  if (values.length === 0) return 0;

  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 === 0) {
    return (sorted[mid - 1] + sorted[mid]) / 2;
  }

  return sorted[mid];
}

/**
 * Get color identity group for categorization
 * Returns 'C' for colorless, 'M' for multicolor, or single color
 */
function getColorGroup(colorIdentity: Color[]): Color | 'C' | 'M' {
  if (colorIdentity.length === 0) return 'C';
  if (colorIdentity.length > 1) return 'M';
  return colorIdentity[0];
}

// ============================================================================
// Computation
// ============================================================================

/**
 * Compute comprehensive deck statistics from deck cards.
 *
 * @param cards - Array of deck cards with full card data
 * @param zones - Zones to include (defaults to commander + companion + main)
 */
export function computeDeckStatistics(cards: EnrichedDeckCard[], zones: string[] = DEFAULT_STATISTICS_ZONES): DeckStatistics {
  // Filter to specified zones
  const relevantCards = cards.filter((c) => zones.includes(c.zone));

  // Basic counts
  let totalCards = 0;
  let landCount = 0;

  // Mana curve tracking (index = CMC, capped at 7)
  const curveDistribution = [0, 0, 0, 0, 0, 0, 0, 0]; // 0-7+
  const cmcValues: number[] = []; // For median calculation

  // Type breakdown
  const typeBreakdown: TypeBreakdown = {
    creatures: 0,
    instants: 0,
    sorceries: 0,
    artifacts: 0,
    enchantments: 0,
    planeswalkers: 0,
    lands: 0,
    battles: 0,
    other: 0,
    creatureRatio: 0,
  };

  // Color distribution
  const colorPips: Record<Color | 'C', number> = { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 };
  const cardsByIdentity: Record<Color | 'C' | 'M', number> = { W: 0, U: 0, B: 0, R: 0, G: 0, C: 0, M: 0 };

  // Process each card
  for (const deckCard of relevantCards) {
    const card = deckCard.card;
    const qty = deckCard.quantity;

    totalCards += qty;

    // Type breakdown
    const cardType = deckCard.classification.type_category;
    const breakdownKey = TYPE_TO_BREAKDOWN_KEY[cardType] ?? 'other';
    typeBreakdown[breakdownKey] += qty;

    // Track lands
    const cardIsLand = deckCard.classification.is_land;
    if (cardIsLand) {
      landCount += qty;
    }

    // Mana curve (exclude lands)
    if (!cardIsLand) {
      const cmcBucket = Math.min(Math.floor(card.cmc ?? 0), 7);
      curveDistribution[cmcBucket] += qty;

      // Add to CMC values for median (one entry per copy)
      for (let i = 0; i < qty; i++) {
        cmcValues.push(card.cmc ?? 0);
      }
    }

    // Color pip counting from mana_cost
    const pips = countManaPips(getDisplayManaCost(card));
    for (const [color, count] of Object.entries(pips) as [Color | 'C', number][]) {
      colorPips[color] += count * qty;
    }

    // Cards by color identity
    const colorGroup = getColorGroup(card.color_identity);
    cardsByIdentity[colorGroup] += qty;
  }

  // Calculate derived statistics
  const totalNonLandCards = totalCards - landCount;
  const cmcSum = cmcValues.reduce((a, b) => a + b, 0);
  const averageCmc = totalNonLandCards > 0 ? cmcSum / totalNonLandCards : 0;
  const medianCmc = median(cmcValues);

  // Creature ratio
  typeBreakdown.creatureRatio = totalNonLandCards > 0 ? typeBreakdown.creatures / totalNonLandCards : 0;

  // Total pips and dominant color
  const totalPips = Object.values(colorPips).reduce((a, b) => a + b, 0);
  let dominantColor: Color | 'C' | null = null;
  let maxPips = 0;
  for (const [color, count] of Object.entries(colorPips) as [Color | 'C', number][]) {
    if (count > maxPips) {
      maxPips = count;
      dominantColor = color;
    }
  }

  return {
    totalCards,
    uniqueCards: relevantCards.length,
    manaCurve: {
      distribution: curveDistribution,
      average: Math.round(averageCmc * 100) / 100, // Round to 2 decimals
      median: Math.round(medianCmc * 100) / 100,
      totalNonLandCards,
    },
    typeBreakdown,
    colorDistribution: {
      pips: colorPips,
      totalPips,
      cardsByIdentity,
      dominantColor: totalPips > 0 ? dominantColor : null,
    },
    landCount,
    spellCount: totalNonLandCards,
  };
}
